import React, { useEffect, useMemo, useRef, useState } from "react";

/* ---------- Hook: in-view trigger for entrance animation ---------- */
function useInView({ threshold = 0.25, rootMargin = "0px 0px -10% 0px" } = {}) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold, rootMargin }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return { ref, active };
}

function HinduWayOfLiving() {
  const { ref: contentRef, active: contentActive } = useInView({
    threshold: 0.2,
    rootMargin: "0px 0px -15% 0px",
  });

  const VIDEO_SRC = "/videos/hindu-way.mp4";

  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // help autoplay
    v.muted = true;

    const p = v.play();
    if (p && typeof p.then === "function") {
      p.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      setIsPlaying(!v.paused);
    }
  }, []);

  const togglePlayPause = async () => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      try {
        await v.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => setIsMuted((m) => !m);



  const themes = useMemo(
    () => [
      { key: "dharma", label: "Dharma — living with integrity" },
      { key: "karma", label: "Karma — responsibility in action" },
      { key: "seva", label: "Seva — service to the world" },
      { key: "ahimsa", label: "Ahimsa — nonviolence & compassion" },
      { key: "wellness", label: "Wellness — Yoga, Ayurveda, meditation" },
      { key: "family", label: "Family & Community Harmony" },
    ],
    []
  );

  return (
    <>
      <style>{`
        html, body { overflow-x: hidden; }

        .howl-content{
          opacity: 0;
          transform: translateY(18px);
          filter: blur(6px);
          transition: opacity 650ms ease, transform 650ms ease, filter 650ms ease;
        }
        .howl-content.is-active{
          opacity: 1;
          transform: translateY(0);
          filter: blur(0);
        }

        .howl-fullVideo{
  width: 90%;
  max-width: 1600px;
  margin: 40px auto;
  height: auto;
  background: #000;
  position: relative;
  overflow: visible;
}

.howl-fullVideo video{
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
}

.a4h-videoControls{
  position: absolute;
  top: auto;
  right: auto;
  bottom: 16px;
  left: 16px;
  display: flex;
  gap: 10px;
  z-index: 3;
}

.a4h-ctrlBtn{
  border: 1px solid rgba(255,255,255,.22);
  background: rgba(0,0,0,.45);
  color: #fff;
  border-radius: 999px;
  padding: 10px 14px;
  font-size: 13px;
  letter-spacing: .02em;
  backdrop-filter: blur(10px);
  cursor: pointer;
  white-space: nowrap;
}

.a4h-ctrlBtn:hover{
  background: rgba(0,0,0,.6);
}
.section-divider{
  width:100%;
  max-width:900px;
  margin:32px auto 38px;

  display:flex;
  align-items:center;
  justify-content:center;
  gap:18px;
}

.section-divider::before,
.section-divider::after{
  content:"";
  flex:1;
  height:3px;

  background: linear-gradient(
    to right,
    transparent,
    #1a2a5a,
    #d4af37,
    transparent
  );

  border-radius:10px;

  box-shadow:
    0 2px 6px rgba(0,0,0,0.18);
}

.section-divider span{
  font-size:26px;
  color:#1a2a5a;
  text-shadow:0 2px 6px rgba(0,0,0,0.15);

  animation: dividerGlow 2.5s ease-in-out infinite;
}
/* Remove line under heading */
.hra-topTitle::after,
.hra-topTitle::before {
  display: none !important;
  content: none !important;
}
.a4h-btn-icon{
  display:none;
}

.a4h-btn-text{
  display:inline;
}
/* =========================
   MOBILE VERSION ONLY
   ========================= */
@media (max-width: 768px) {

  /* Remove extra top spacing */
  .block.color-scheme-4{
    padding-top: 0 !important;
    margin-top: 0 !important;
  }

  .section-sub-title{
  margin-top: 15px !important;
  padding-top: 20px !important;
}

  /* Mobile heading */
  .hra-topTitle{
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    gap: 2px !important;
    text-align: center !important;
  }

  .hra-mainTitle{
    display: block !important;
    width: 100%;
    font-size: 24px !important;
    line-height: 1.15 !important;
  }
  .hra-mainTitle:first-child{
    margin-bottom: 0 !important;
  }

  /* Second line */
  .hra-mainTitle:nth-child(2){
    font-size: 22px !important;
    margin-top: 0 !important;
  }
  .hra-subTitle{
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 0 10px;
     margin-bottom: 0 !important;
  padding-bottom: 0 !important;
  }

  .hra-topIntro{
    display: none !important;
  }

  /* Divider */
  .section-divider{
    margin: 12px auto 16px !important;
    gap: 10px !important;
  }

  .section-divider span{
    font-size: 18px !important;
  }

  .section-divider::before,
  .section-divider::after{
    height: 2px !important;
  }

  /* Video section */
  .howl-fullVideo{
    width: 100% !important;
    margin: 20px auto !important;
    aspect-ratio: 16 / 9 !important;
    border-radius: 0 !important;
    margin-top: -50px !important;
    margin-bottom: 20px !important;
    overflow: hidden !important;
  }

  .howl-fullVideo video{
    object-fit: contain !important;
    width: 100% !important;
    height: 100% !important;
  }

  /* Match other pages: dark pill controls top-right */
  .a4h-videoControls{
    position: absolute !important;
    top: auto !important;
    right: auto !important;
    left: 6px !important;
    bottom: 6px !important;
    gap: 4px !important;
    flex-direction: row !important;
    z-index: 5 !important;
  }

  .a4h-btn-text{
  display:none;
}

.a4h-btn-icon{
  display:inline;
  font-size:20px;
  line-height:1;
}

.a4h-videoControls{
  bottom:6px !important;
  left:6px !important;
  gap:4px !important;
}

.a4h-ctrlBtn{
  background:transparent !important;
  border:none !important;
  box-shadow:none !important;
  backdrop-filter:none !important;
  padding:0 !important;
  width:auto !important;
  height:auto !important;
  min-width:auto !important;
  border-radius:0 !important;
  color:#fff;
  cursor:pointer;
}

.a4h-ctrlBtn:hover,
.a4h-ctrlBtn:focus,
.a4h-ctrlBtn:active{
  background:transparent !important;
  box-shadow:none !important;
}

  /* Content area */
  .howl-content{
    padding: 0 8px !important;
  }

  .howl-content h3{
    font-size: 20px !important;
    margin-bottom: 12px !important;
  }

  /* Cards */
  .howl-content .col-12 > div,
  .howl-content .col-md-6 > div{
    padding: 14px !important;
    border-radius: 12px !important;
  }

  /* Lists */
  .arrow-list {
    list-style: none !important;
    padding-left: 0 !important;
    margin: 0 !important;
  }

  .arrow-list li {
    position: relative;
    padding-left: 24px !important;
    margin-bottom: 12px !important;
    line-height: 1.6 !important;
    font-size: 14px !important;
    display: block !important;
  }

  .arrow-list li::before {
    content: "➤";
    position: absolute;
    left: 0;
    top: 0;
    font-size: 12px;
    color: #7a0000;
  }

  /* Stack columns neatly */
  .row.g-4{
    --bs-gutter-y: 1rem !important;
  }

  /* Prevent horizontal scroll */
  .container,
  .row,
  .col-12,
  .col-md-6{
    overflow-x: hidden;
  }
  .block.color-scheme-4{
    padding-top: 0 !important;
    margin-top: 0 !important;
  }

  

  .container:first-child{
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  /* Force remove any empty hero/banner */
  .inner-banner,
  .page-banner,
  .hero-banner,
  .breadcrumb-banner,
  .banner-area{
    display:none !important;
  }
  
}        
      `}</style>

      <section>
        <div className="block color-scheme-4">

          {/* 1️⃣ Heading */}
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-9">
                <div className="section-sub-title center">
                  <article className="section-title-body white">
                    <h1 className="head-title hra-topTitle">
  <span className="hra-mainTitle">
    Hindu Way of Living
  </span>
  <span className="hra-mainTitle">
   (Duty towards Dharma)
  </span>
<div className="section-divider">
  <span>✦ ✦ ✦</span>
</div>
  <span className="hra-subTitle">
    (VALUES, WELLNESS & CULTURAL ETHOS)
  </span>
</h1>
                    <p className="hra-topIntro">
                      This section explores timeless Sanatana Dharma principles
                      that guide personal growth and community well-being.
                    </p>
                  </article>
                </div>
              </div>
            </div>
          </div>

          {/* 2️⃣ Full-width video */}
          <div className="howl-fullVideo">
            <video
              ref={videoRef}
              src={VIDEO_SRC}
              playsInline
              autoPlay
              loop
              muted={isMuted}
              preload="metadata"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            <div className="a4h-videoControls">
  <button
    type="button"
    className="a4h-ctrlBtn"
    onClick={togglePlayPause}
  >
    <span className="a4h-btn-text">
      {isPlaying ? "Pause ⏸️" : "Play ▶️"}
    </span>
    <span className="a4h-btn-icon">
      {isPlaying ? "⏸️" : "▶️"}
    </span>
  </button>

  <button
    type="button"
    className="a4h-ctrlBtn"
    onClick={toggleMute}
  >
    <span className="a4h-btn-text">
      {isMuted ? "Unmute 🔊" : "Mute 🔇"}
    </span>
    <span className="a4h-btn-icon">
      {isMuted ? "🔇" : "🔊"}
    </span>
  </button>
</div>
          </div>

          {/* 3️⃣ Rearranged content */}
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-9">
                <article
                  ref={contentRef}
                  className={`section-title-body white howl-content ${
                    contentActive ? "is-active" : ""
                  }`}
                >
                  <div className="row g-4 mt-4">

                    {/* Highlights – full width */}
                    <div className="col-12">
                      <div
                        style={{
                          borderRadius: 16,
                          padding: 16,
                          background: "rgba(255,255,255,0.08)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          textAlign: "left",
                        }}
                      >
                        <h3 className="white">Highlights</h3>
                        <ul className="arrow-list" >
                          <li>Values-led daily living</li>
                          <li>Wellness practices (Yoga, Ayurveda, meditation)</li>
                          <li>Compassion, service, and community harmony</li>
                          <li>Family learning and youth-focused resources</li>
                        </ul>
                      </div>
                    </div>

                    {/* Core Themes */}
                    <div className="col-12 col-md-6 ">
                      <h3 className="white">Core Themes</h3>
                      <ul className="arrow-list">
                        {themes.map((t) => (
                          <li key={t.key}>{t.label}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Resource Library */}
                    <div className="col-12 col-md-6">
                      <div
                        style={{
                          borderRadius: 16,
                          padding: 16,
                          background: "rgba(255,255,255,0.08)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          textAlign: "left",
                        }}
                      >
                        
                      </div>
                    </div>

                  </div>
                </article>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}

export default HinduWayOfLiving;
